import {defineMessages} from 'react-intl'

/**
 * Catálogo de Plantillas (`/templates`). Namespace raíz de la Fase 1.9: `comms.*`.
 *
 * No se traducen: los nombres de canal (Email/SMS/WhatsApp/Telegram), el
 * contenido de los starters (`content.*` viaja al backend como dato de la
 * plantilla) ni los tokens de personalización `{{first_name}}` y compañía —
 * son literales del motor de personalización y romperlos rompe el envío.
 */
export const TEMPLATE_MESSAGES = defineMessages({
  // ── Página ──
  pageTitle: {id: 'comms.templates.page.title', defaultMessage: 'Messaging templates'},
  pageSubtitle: {
    id: 'comms.templates.page.subtitle',
    defaultMessage:
      'Centralize SMS, email, WhatsApp and Telegram content. Create multi-channel variants for each template.',
  },
  pageLoadError: {
    id: 'comms.templates.page.loadError',
    defaultMessage: 'Unable to load templates. {message}',
  },

  // ── Selector de plantilla inicial ──
  starterTitle: {
    id: 'comms.templates.starter.title',
    defaultMessage: 'Choose a Template Starter',
  },
  starterSubtitle: {
    id: 'comms.templates.starter.subtitle',
    defaultMessage: 'Start with a professional template or create your own from scratch',
  },
  starterEmailTab: {
    id: 'comms.templates.starter.emailTab',
    defaultMessage: 'Email Templates',
  },
  starterSmsTab: {id: 'comms.templates.starter.smsTab', defaultMessage: 'SMS Templates'},
  starterSmsHint: {
    id: 'comms.templates.starter.smsHint',
    defaultMessage:
      'SMS messages are limited to <b>{limit} characters</b> per segment. Variables like {variable} will expand when sent.',
  },
  starterUseTemplate: {
    id: 'comms.templates.starter.useTemplate',
    defaultMessage: 'Use This Template',
  },
  starterCharsSegments: {
    id: 'comms.templates.starter.charsSegments',
    defaultMessage: '{chars} chars · {segments} seg',
  },
  starterFromScratch: {
    id: 'comms.templates.starter.fromScratch',
    defaultMessage: 'Start from Scratch',
  },

  // ── Galería de medios ──
  mediaTitle: {id: 'comms.templates.media.title', defaultMessage: 'Media Gallery'},
  mediaSearch: {id: 'comms.templates.media.search', defaultMessage: 'Search images...'},
  mediaUploadNew: {id: 'comms.templates.media.uploadNew', defaultMessage: 'Upload New'},
  mediaEmpty: {id: 'comms.templates.media.empty', defaultMessage: 'No images found'},
  mediaEmptyHint: {
    id: 'comms.templates.media.emptyHint',
    defaultMessage: 'Upload a new image to get started',
  },
  mediaUploadNow: {id: 'comms.campaigns.media.uploadNow', defaultMessage: 'Upload Now'},

  // ── Reutilizadas ──
  // ── Editor de variantes ──
  variantEditTitle: {
    id: 'comms.templates.variant.editTitle',
    defaultMessage: 'Edit Variant - {channel}',
  },
  variantNewTitle: {
    id: 'comms.templates.variant.newTitle',
    defaultMessage: 'New Variant - {channel}',
  },
  subject: {id: 'common.field.subject', defaultMessage: 'Subject'},
  subjectPlaceholder: {
    id: 'comms.templates.variant.subjectPlaceholder',
    defaultMessage: 'Example: Welcome to our service',
  },
  variables: {id: 'comms.templates.variant.variables', defaultMessage: 'Variables'},
  availableVariables: {
    id: 'comms.templates.variant.availableVariables',
    defaultMessage: 'Available variables',
  },
  tabContent: {id: 'comms.templates.variant.tabContent', defaultMessage: 'Content'},
  tabStyles: {id: 'comms.templates.variant.tabStyles', defaultMessage: 'Styles'},
  visualEditor: {id: 'comms.templates.variant.visualEditor', defaultMessage: 'Visual Editor'},
  viewHtml: {id: 'comms.templates.variant.viewHtml', defaultMessage: 'View HTML'},
  sendTest: {id: 'comms.templates.variant.sendTest', defaultMessage: 'Send Test'},
  sectionHeader: {id: 'comms.templates.variant.sectionHeader', defaultMessage: 'Header'},
  sectionBody: {id: 'comms.templates.variant.sectionBody', defaultMessage: 'Body'},
  sectionFooter: {id: 'comms.templates.variant.sectionFooter', defaultMessage: 'Footer'},
  sectionDisclaimer: {
    id: 'comms.templates.variant.sectionDisclaimer',
    defaultMessage: 'Disclaimer',
  },
  disclaimerText: {
    id: 'comms.templates.variant.disclaimerText',
    defaultMessage: 'Disclaimer Text',
  },
  unsubscribeText: {
    id: 'comms.templates.variant.unsubscribeText',
    defaultMessage: 'Unsubscribe Link Text',
  },
  disclaimerHint: {
    id: 'comms.templates.variant.disclaimerHint',
    defaultMessage: 'This section is always included in marketing emails and uses {variable}.',
  },
  visualEditorOff: {
    id: 'comms.templates.variant.visualEditorOff',
    defaultMessage:
      'Activate "Use Visual Editor" to design the body of the email. If you prefer plain text, use the field below.',
  },
  waTitle: {id: 'comms.templates.variant.waTitle', defaultMessage: 'Meta WhatsApp Templates'},
  waHint: {
    id: 'comms.templates.variant.waHint',
    defaultMessage: 'For marketing campaigns, you must use templates approved by Meta.',
  },
  waStep1: {
    id: 'comms.templates.variant.waStep1',
    defaultMessage: 'Create templates in Meta Business Manager',
  },
  waStep2: {
    id: 'comms.templates.variant.waStep2',
    defaultMessage: 'Wait for approval (24-48h)',
  },
  waStep3: {id: 'comms.templates.variant.waStep3', defaultMessage: 'Enter the ID here'},
  waTemplateId: {
    id: 'comms.templates.variant.waTemplateId',
    defaultMessage: 'Meta WhatsApp Template ID (optional)',
  },
  waTemplateIdPlaceholder: {
    id: 'comms.templates.variant.waTemplateIdPlaceholder',
    defaultMessage: 'e.g. payment_reminder',
  },
  waTemplateIdHint: {
    id: 'comms.templates.variant.waTemplateIdHint',
    defaultMessage: "If you don't use a template, it will only work in active sessions (24h)",
  },
  waLanguage: {id: 'comms.templates.variant.waLanguage', defaultMessage: 'Language'},
  langSpanish: {id: 'common.language.spanish', defaultMessage: 'Spanish'},
  langEnglish: {id: 'common.language.english', defaultMessage: 'English'},
  langPortuguese: {
    id: 'comms.templates.variant.langPortuguese',
    defaultMessage: 'Portuguese',
  },
  messageBody: {id: 'comms.templates.variant.messageBody', defaultMessage: 'Message Body'},
  messageBodyFallback: {
    id: 'comms.templates.variant.messageBodyFallback',
    defaultMessage: '(Text Plain / Fallback)',
  },
  bodyPlaceholder: {
    id: 'comms.templates.variant.bodyPlaceholder',
    defaultMessage: 'e.g. Hello {token}, your payment is due soon',
  },
  waToolbarTip: {
    id: 'comms.templates.variant.waToolbarTip',
    defaultMessage:
      '<b>Tip:</b> Use the <b>Variables</b> button in the toolbar to insert variables easily. You can also write manually using {variable}',
  },
  tipClickVariable: {
    id: 'comms.templates.variant.tipClickVariable',
    defaultMessage: '<b>Tip:</b> Click a variable to insert it, or type manually using {variable}',
  },
  tipClickVariables: {
    id: 'comms.templates.variant.tipClickVariables',
    defaultMessage:
      '<b>Tip:</b> Click on variables above to insert them, or type manually using {variable} format',
  },
  smsCharacters: {
    id: 'comms.templates.variant.smsCharacters',
    defaultMessage: 'Characters: <b>{count}</b>',
  },
  smsSegments: {
    id: 'comms.templates.variant.smsSegments',
    defaultMessage: '{count} SMS',
  },
  smsCostHint: {
    id: 'comms.templates.variant.smsCostHint',
    defaultMessage: '(~{count}x cost)',
  },
  smsPart: {
    id: 'comms.templates.variant.smsPart',
    defaultMessage: 'Part {part}: {used}/{capacity}',
  },
  smsUnicode: {
    id: 'comms.templates.variant.smsUnicode',
    defaultMessage: 'Unicode detected — limit reduced to {single}/{part} chars',
  },
  markDefault: {
    id: 'comms.templates.variant.markDefault',
    defaultMessage: 'Mark as default variant',
  },
  updateVariant: {id: 'comms.templates.variant.update', defaultMessage: 'Update Variant'},
  createVariant: {id: 'comms.templates.variant.create', defaultMessage: 'Create Variant'},
  deleteVariant: {id: 'comms.templates.variant.delete', defaultMessage: 'Delete Variant'},

  // ── Modal de HTML final ──
  htmlModalTitle: {
    id: 'comms.templates.html.title',
    defaultMessage: 'Final HTML Email',
  },
  htmlModalHint: {
    id: 'comms.templates.html.hint',
    defaultMessage:
      'This is the complete HTML code that will be used to send the email. You can copy it and use it in any email sending platform.',
  },
  htmlCopy: {id: 'comms.templates.html.copy', defaultMessage: 'Copy HTML'},
  htmlDownload: {id: 'comms.templates.html.download', defaultMessage: 'Download HTML'},
  htmlCopied: {
    id: 'comms.templates.html.copied',
    defaultMessage: 'HTML copied to clipboard',
  },

  // ── Modal de correo de prueba ──
  testTitle: {id: 'comms.templates.test.title', defaultMessage: 'Send Test Email'},
  testHint: {
    id: 'comms.templates.test.hint',
    defaultMessage:
      '<b>Tip:</b> Send the email to your own address to verify how it looks in different email clients (Gmail, Outlook, etc.)',
  },
  testRecipient: {
    id: 'comms.templates.test.recipient',
    defaultMessage: 'Email of the Recipient',
  },
  testRecipientPlaceholder: {
    id: 'comms.templates.test.recipientPlaceholder',
    defaultMessage: 'you@email.com',
  },
  testRecipientHint: {
    id: 'comms.templates.test.recipientHint',
    defaultMessage: 'You can use multiple emails separated by commas',
  },
  testAdvanced: {
    id: 'comms.templates.test.advanced',
    defaultMessage: 'Advanced Options (Optional)',
  },
  testFromEmail: {id: 'comms.templates.test.fromEmail', defaultMessage: 'From Email'},
  testFromEmailPlaceholder: {
    id: 'comms.templates.test.fromEmailPlaceholder',
    defaultMessage: 'mailing@yourcompany.com (optional)',
  },
  testFromEmailHint: {
    id: 'comms.templates.test.fromEmailHint',
    defaultMessage: 'Leave empty to use the default email',
  },
  testFromName: {
    id: 'comms.templates.test.fromName',
    defaultMessage: 'From Name (Name of the sender)',
  },
  testFromNamePlaceholder: {
    id: 'comms.templates.test.fromNamePlaceholder',
    defaultMessage: 'Marketing Team (optional)',
  },
  testReplyTo: {id: 'comms.templates.test.replyTo', defaultMessage: 'Reply-To'},
  testReplyToPlaceholder: {
    id: 'comms.templates.test.replyToPlaceholder',
    defaultMessage: 'support@yourcompany.com (optional)',
  },
  testReplyToHint: {
    id: 'comms.templates.test.replyToHint',
    defaultMessage: 'Where the user responses will go',
  },
  testIncludeLogo: {
    id: 'comms.templates.test.includeLogo',
    defaultMessage: 'Embed logo in the email (optional)',
  },
  testSummarySubject: {
    id: 'comms.templates.test.summarySubject',
    defaultMessage: '<b>Subject:</b> {subject}',
  },
  testSummaryBlocks: {
    id: 'comms.templates.test.summaryBlocks',
    defaultMessage: '<b>Blocks:</b> {count, plural, one {# block} other {# blocks}} in total',
  },
  testDefaultSubject: {
    id: 'comms.templates.test.defaultSubject',
    defaultMessage: 'Test Email',
  },
  testSending: {id: 'comms.templates.test.sending', defaultMessage: 'Sending...'},
  testInvalidEmail: {
    id: 'comms.templates.test.invalidEmail',
    defaultMessage: 'Please enter a valid email',
  },
  testSent: {id: 'comms.templates.test.sent', defaultMessage: 'Test email sent to {email}'},
  testError: {
    id: 'comms.templates.test.error',
    defaultMessage: 'Could not send the test email: {message}',
  },

  // ── Vista previa ──
  previewLive: {id: 'comms.templates.preview.live', defaultMessage: 'Live Preview'},
  previewDesktop: {id: 'comms.templates.preview.desktop', defaultMessage: 'Desktop View'},
  previewCharacters: {
    id: 'comms.templates.preview.characters',
    defaultMessage: '{count, plural, one {# character} other {# characters}}',
  },
  previewMax: {id: 'comms.templates.preview.max', defaultMessage: '/ {max} max'},
  previewSubject: {
    id: 'comms.templates.preview.subject',
    defaultMessage: '<b>Subject:</b> {subject}',
  },
  previewWaEmpty: {
    id: 'comms.templates.preview.waEmpty',
    defaultMessage: 'Add text to the variant to see the WhatsApp preview.',
  },
  previewTemplateId: {
    id: 'comms.templates.preview.templateId',
    defaultMessage: '<b>Template ID:</b> {id} ({language})',
  },

  // ── Ajustes de diseño ──
  designSection: {id: 'comms.templates.design.section', defaultMessage: 'Brand & Styles'},
  designPrimaryColor: {
    id: 'comms.templates.design.primaryColor',
    defaultMessage: 'Primary Color (Buttons/Links)',
  },
  designFontFamily: {
    id: 'comms.templates.design.fontFamily',
    defaultMessage: 'Font Family',
  },

  // ── Editor HTML ──
  htmlEditorLabel: {id: 'comms.templates.htmlEditor.label', defaultMessage: 'HTML Editor'},
  htmlEditorMode: {id: 'comms.templates.htmlEditor.mode', defaultMessage: 'Editor Mode'},
  htmlEditorViewCode: {
    id: 'comms.templates.htmlEditor.viewCode',
    defaultMessage: 'View Code',
  },
  htmlEditorVisual: {
    id: 'comms.templates.htmlEditor.visual',
    defaultMessage: 'Visual Edit',
  },
  htmlEditorRawHint: {
    id: 'comms.templates.htmlEditor.rawHint',
    defaultMessage: 'Editing raw HTML. Switch to Visual Editor for WYSIWYG editing.',
  },
  htmlEditorToolbarHint: {
    id: 'comms.templates.htmlEditor.toolbarHint',
    defaultMessage:
      'Use the toolbar above for rich formatting. Switch to "View Code" to see/edit raw HTML.',
  },
  htmlEditorVariablesHint: {
    id: 'comms.templates.htmlEditor.variablesHint',
    defaultMessage: 'Variables: Use {variable} format.',
  },

  // ── Toasts de la página ──
  toastBlueprintReadOnly: {
    id: 'comms.templates.toast.blueprintReadOnly',
    defaultMessage:
      'Only administrators can edit blueprint templates. You can duplicate it to create your own copy.',
  },
  toastUpdated: {id: 'comms.templates.toast.updated', defaultMessage: 'Template updated'},
  toastCreated: {id: 'comms.templates.toast.created', defaultMessage: 'Template created'},
  toastSaveError: {
    id: 'comms.templates.toast.saveError',
    defaultMessage: 'Unable to save template',
  },
  toastDuplicated: {
    id: 'comms.templates.toast.duplicated',
    defaultMessage: 'Template duplicated',
  },
  toastDuplicateError: {
    id: 'comms.templates.toast.duplicateError',
    defaultMessage: 'Could not duplicate template',
  },
  toastBlueprintAdminOnly: {
    id: 'comms.templates.toast.blueprintAdminOnly',
    defaultMessage: 'Only administrators can change blueprint status',
  },
  toastBlueprintError: {
    id: 'comms.templates.toast.blueprintError',
    defaultMessage: 'Could not update blueprint status',
  },

  // ── Página del editor ──
  editorNotFound: {
    id: 'comms.templates.editor.notFound',
    defaultMessage: 'Template not found.',
  },
  editorBackToList: {
    id: 'comms.templates.editor.backToList',
    defaultMessage: 'Back to the list',
  },
  editorMarkBlueprint: {
    id: 'comms.templates.editor.markBlueprint',
    defaultMessage: 'Mark as Blueprint',
  },
  editorDuplicating: {
    id: 'comms.templates.editor.duplicating',
    defaultMessage: 'Duplicating...',
  },
  editorDuplicateBlueprint: {
    id: 'comms.templates.editor.duplicateBlueprint',
    defaultMessage: 'Duplicate Blueprint',
  },
  editorEdit: {id: 'comms.templates.editor.edit', defaultMessage: 'Edit'},
  editorPreview: {id: 'comms.templates.editor.preview', defaultMessage: 'Preview'},
  editorReadOnly: {
    id: 'comms.templates.editor.readOnly',
    defaultMessage:
      'This blueprint is read-only for your role. Only administrators can edit blueprint templates. Duplicate it to create your own editable template.',
  },
  editorActiveChannels: {
    id: 'comms.templates.editor.activeChannels',
    defaultMessage: 'Active channels',
  },
  editorVariantsCreated: {
    id: 'comms.templates.editor.variantsCreated',
    defaultMessage: 'Variants created',
  },
  editorVariantsCount: {
    id: 'comms.templates.editor.variantsCount',
    defaultMessage: '{created} / {total}',
  },
  editorBlueprintYes: {
    id: 'comms.templates.editor.blueprintYes',
    defaultMessage: 'Yes — base template',
  },
  editorPreviewTitle: {
    id: 'comms.templates.editor.previewTitle',
    defaultMessage: 'Preview ({channel})',
  },
  editorNoVariant: {id: 'comms.templates.editor.noVariant', defaultMessage: 'No Variant'},
  editorNoVariantHint: {
    id: 'comms.templates.editor.noVariantHint',
    defaultMessage: 'Create a variant to see the preview',
  },
  editorSaving: {id: 'comms.templates.editor.saving', defaultMessage: 'Saving changes...'},

  // ── Barra de herramientas del editor de texto ──
  rteBold: {id: 'comms.templates.rte.bold', defaultMessage: 'Bold'},
  rteItalic: {id: 'comms.templates.rte.italic', defaultMessage: 'Italic'},
  rteUnderline: {id: 'comms.templates.rte.underline', defaultMessage: 'Underline'},
  rteStrike: {id: 'comms.templates.rte.strike', defaultMessage: 'Strikethrough'},
  rteHeading1: {id: 'comms.templates.rte.heading1', defaultMessage: 'Heading 1'},
  rteHeading2: {id: 'comms.templates.rte.heading2', defaultMessage: 'Heading 2'},
  rteHeading3: {id: 'comms.templates.rte.heading3', defaultMessage: 'Heading 3'},
  rteFontSize: {id: 'comms.templates.rte.fontSize', defaultMessage: 'Font Size'},
  rteTextColor: {id: 'comms.templates.rte.textColor', defaultMessage: 'Text Color'},
  rteBulletList: {id: 'comms.templates.rte.bulletList', defaultMessage: 'Bullet List'},
  rteNumberedList: {id: 'comms.templates.rte.numberedList', defaultMessage: 'Numbered List'},
  rteAlignLeft: {id: 'comms.templates.rte.alignLeft', defaultMessage: 'Align Left'},
  rteAlignCenter: {id: 'comms.templates.rte.alignCenter', defaultMessage: 'Align Center'},
  rteAlignRight: {id: 'comms.templates.rte.alignRight', defaultMessage: 'Align Right'},
  rteInsertLink: {id: 'comms.templates.rte.insertLink', defaultMessage: 'Insert Link'},
  rteInsertImage: {
    id: 'comms.templates.rte.insertImage',
    defaultMessage: 'Insert Image (Media Manager)',
  },
  rteBlockquote: {id: 'comms.templates.rte.blockquote', defaultMessage: 'Blockquote'},
  rteInsertButton: {
    id: 'comms.templates.rte.insertButton',
    defaultMessage: 'Insert Button (CTA)',
  },
  rteInsertVariable: {
    id: 'comms.templates.rte.insertVariable',
    defaultMessage: 'Insert Variable',
  },
  rteInsertIcon: {id: 'comms.templates.rte.insertIcon', defaultMessage: 'Insert Icon'},
  rteIcons: {id: 'comms.templates.rte.icons', defaultMessage: 'Icons'},
  rteIconsGroup: {
    id: 'comms.templates.rte.iconsGroup',
    defaultMessage: 'Icons (Insurance for Email)',
  },
  waOnlyFormats: {
    id: 'comms.templates.wa.onlyFormats',
    defaultMessage: 'WhatsApp formats only',
  },
  iconPhone: {id: 'comms.templates.icon.phone', defaultMessage: 'Phone'},
  iconMobile: {id: 'comms.templates.icon.mobile', defaultMessage: 'Mobile'},
  iconEmail: {id: 'common.field.email', defaultMessage: 'Email'},
  iconChat: {id: 'comms.templates.icon.chat', defaultMessage: 'Chat'},
  iconLocation: {id: 'common.field.location', defaultMessage: 'Location'},
  iconWeb: {id: 'comms.templates.icon.web', defaultMessage: 'Web'},
  iconCheck: {id: 'comms.templates.icon.check', defaultMessage: 'Check'},
  iconStar: {id: 'comms.templates.icon.star', defaultMessage: 'Star'},
  iconCalendar: {id: 'MENU.item.calendar', defaultMessage: 'Calendar'},
  iconClock: {id: 'comms.templates.icon.clock', defaultMessage: 'Clock'},
  iconWarning: {id: 'comms.templates.icon.warning', defaultMessage: 'Warning'},
  iconArrow: {id: 'comms.templates.icon.arrow', defaultMessage: 'Arrow'},
  iconList: {id: 'comms.templates.icon.list', defaultMessage: 'List'},
  iconHealth: {id: 'comms.templates.icon.health', defaultMessage: 'Health'},
  iconInsurance: {id: 'comms.templates.icon.insurance', defaultMessage: 'Insurance'},
  waPlaceholder: {
    id: 'comms.templates.wa.placeholder',
    defaultMessage: 'Write your message here...',
  },
  rteClearFormatting: {
    id: 'comms.templates.rte.clearFormatting',
    defaultMessage: 'Clear Formatting',
  },

  // ── Editor de WhatsApp ──
  waBold: {id: 'comms.templates.wa.bold', defaultMessage: 'Bold (*text*)'},
  waItalic: {id: 'comms.templates.wa.italic', defaultMessage: 'Italic (_text_)'},
  waStrike: {id: 'comms.templates.wa.strike', defaultMessage: 'Strikethrough (~text~)'},
  waMonospace: {
    id: 'comms.templates.wa.monospace',
    defaultMessage: 'Monospace (```text```)',
  },
  waEmojis: {id: 'comms.templates.wa.emojis', defaultMessage: 'Emojis'},
  waCommonEmojis: {
    id: 'comms.templates.wa.commonEmojis',
    defaultMessage: 'Common emojis',
  },
  waFormatsHint: {
    id: 'comms.templates.wa.formatsHint',
    defaultMessage: '<b>Formats:</b> *bold* • _italic_ • ~strikethrough~ • ```monospace```',
  },
  waPreviewTitle: {
    id: 'comms.templates.wa.previewTitle',
    defaultMessage: 'WhatsApp Preview',
  },
  waPreviewSubtitle: {
    id: 'comms.templates.wa.previewSubtitle',
    defaultMessage: 'This is how your message will look',
  },
  waPreviewFooter: {
    id: 'comms.templates.wa.previewFooter',
    defaultMessage: 'Emojis and variables are rendered in the real message',
  },
  waHeaderImage: {id: 'comms.templates.wa.headerImage', defaultMessage: 'Image'},
  waHeaderVideo: {id: 'comms.templates.wa.headerVideo', defaultMessage: 'Video'},
  waHeaderDocument: {id: 'comms.templates.wa.headerDocument', defaultMessage: 'Document'},
  waHeaderAlt: {id: 'comms.templates.wa.headerAlt', defaultMessage: 'Header'},

  // ── Constructor de bloques ──
  builderLocked: {id: 'comms.templates.builder.locked', defaultMessage: 'Locked'},
  builderLockReason: {
    id: 'comms.templates.builder.lockReason',
    defaultMessage: 'This block is locked to preserve the core message.',
  },
  builderEditText: {id: 'comms.templates.builder.editText', defaultMessage: 'Edit Text'},
  builderEditNote: {id: 'comms.templates.builder.editNote', defaultMessage: 'Edit Note'},
  builderEditButton: {
    id: 'comms.templates.builder.editButton',
    defaultMessage: 'Edit Button',
  },
  builderCol1: {id: 'comms.templates.builder.col1', defaultMessage: 'Col 1'},
  builderCol2: {id: 'comms.templates.builder.col2', defaultMessage: 'Col 2'},
  builderColShort1: {id: 'comms.templates.builder.colShort1', defaultMessage: 'C1'},
  builderColShort2: {id: 'comms.templates.builder.colShort2', defaultMessage: 'C2'},
  builderColShort3: {id: 'comms.templates.builder.colShort3', defaultMessage: 'C3'},
  builderEmptyTitle: {
    id: 'comms.templates.builder.emptyTitle',
    defaultMessage: 'Your canvas is empty',
  },
  builderEmptyHint: {
    id: 'comms.templates.builder.emptyHint',
    defaultMessage: 'Select a block from the left or use a quick action:',
  },
  builderAddColumn: {
    id: 'comms.templates.builder.addColumn',
    defaultMessage: '+ 1 Column',
  },
  builderAddHero: {id: 'comms.templates.builder.addHero', defaultMessage: '+ Hero'},
  builderAddTwoColumns: {
    id: 'comms.templates.builder.addTwoColumns',
    defaultMessage: '+ 2 Columns',
  },
  builderEditContent: {
    id: 'comms.templates.builder.editContent',
    defaultMessage: 'Edit Content',
  },
  builderAddContentHint: {
    id: 'comms.templates.builder.addContentHint',
    defaultMessage: 'Click "Edit Text" to add content',
  },
  builderDeleteTitle: {
    id: 'comms.templates.builder.deleteTitle',
    defaultMessage: 'Delete Block',
  },
  builderDeleteConfirm: {
    id: 'comms.templates.builder.deleteConfirm',
    defaultMessage: 'Are you sure you want to delete this block?',
  },
  builderBlockType: {
    id: 'comms.templates.builder.blockType',
    defaultMessage: 'Block type: <b>{type}</b>',
  },
  builderDeleteHint: {
    id: 'comms.templates.builder.deleteHint',
    defaultMessage: 'This action cannot be undone and any content in this block will be lost.',
  },
  builderUnknownBlock: {
    id: 'comms.templates.builder.unknownBlock',
    defaultMessage: 'Unknown',
  },
  sidebarStructure: {
    id: 'comms.templates.builder.sidebarStructure',
    defaultMessage: 'Structure',
  },
  sidebarComponents: {
    id: 'comms.templates.builder.sidebarComponents',
    defaultMessage: 'Components',
  },
  sidebarMedia: {id: 'comms.templates.builder.sidebarMedia', defaultMessage: 'Media'},

  // ── Reutilizadas ──
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  delete: {id: 'common.action.delete', defaultMessage: 'Delete'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  type: {id: 'common.field.type', defaultMessage: 'Type'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},
  blueprint: {id: 'comms.campaigns.templates.blueprint', defaultMessage: 'Blueprint'},
  markBlueprint: {
    id: 'comms.campaigns.templates.markBlueprint',
    defaultMessage: 'Mark as blueprint',
  },
  unmarkBlueprint: {
    id: 'comms.campaigns.templates.unmarkBlueprint',
    defaultMessage: 'Remove blueprint status',
  },
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  back: {id: 'common.action.back', defaultMessage: 'Back'},
})

/**
 * Nombre y descripción de cada plantilla inicial, indexados por su `id`.
 * El EN de `constants/templateStarters.ts` sigue siendo el canónico: es lo que
 * se persiste al crear la plantilla a partir del starter.
 */
export const STARTER_MESSAGES = defineMessages({
  'newsletter.name': {
    id: 'comms.templates.starter.newsletter.name',
    defaultMessage: 'Newsletter',
  },
  'newsletter.description': {
    id: 'comms.templates.starter.newsletter.description',
    defaultMessage: 'Regular updates, company news, and blog posts',
  },
  'welcome-insurance.name': {
    id: 'comms.templates.starter.welcome.name',
    defaultMessage: 'Welcome New Client',
  },
  'welcome-insurance.description': {
    id: 'comms.templates.starter.welcome.description',
    defaultMessage: 'Warm welcome email when a new policy is created or completed',
  },
  'policy-renewal.name': {
    id: 'comms.templates.starter.renewal.name',
    defaultMessage: 'Policy Renewal Reminder',
  },
  'policy-renewal.description': {
    id: 'comms.templates.starter.renewal.description',
    defaultMessage: 'Alert clients that their policy is coming up for renewal',
  },
  'cross-sell-home.name': {
    id: 'comms.templates.starter.crossSellHome.name',
    defaultMessage: 'Cross-sell: Add Home Insurance',
  },
  'cross-sell-home.description': {
    id: 'comms.templates.starter.crossSellHome.description',
    defaultMessage: 'Offer home coverage to clients who already have auto insurance',
  },
  'cancellation-notice.name': {
    id: 'comms.templates.starter.cancellation.name',
    defaultMessage: 'Policy Cancellation Notice',
  },
  'cancellation-notice.description': {
    id: 'comms.templates.starter.cancellation.description',
    defaultMessage: 'Reach out when a policy status changes to prevent coverage gaps',
  },
  'informational.name': {
    id: 'comms.templates.starter.informational.name',
    defaultMessage: 'Informational',
  },
  'informational.description': {
    id: 'comms.templates.starter.informational.description',
    defaultMessage: 'Clean layout for announcements, updates, and educational content',
  },
  'sms-birthday.name': {
    id: 'comms.templates.starter.smsBirthday.name',
    defaultMessage: 'Birthday Greeting',
  },
  'sms-birthday.description': {
    id: 'comms.templates.starter.smsBirthday.description',
    defaultMessage: 'Send a warm birthday message to your clients',
  },
  'sms-policy-reminder.name': {
    id: 'comms.templates.starter.smsRenewal.name',
    defaultMessage: 'Policy Renewal Reminder',
  },
  'sms-policy-reminder.description': {
    id: 'comms.templates.starter.smsRenewal.description',
    defaultMessage: 'Quick SMS reminder that a policy renewal is coming up',
  },
  'sms-followup.name': {
    id: 'comms.templates.starter.smsFollowup.name',
    defaultMessage: 'Lead Follow-up',
  },
  'sms-followup.description': {
    id: 'comms.templates.starter.smsFollowup.description',
    defaultMessage: "Re-engage a lead that hasn't responded recently",
  },
  'sms-cross-sell.name': {
    id: 'comms.templates.starter.smsCrossSell.name',
    defaultMessage: 'Cross-sell Opportunity',
  },
  'sms-cross-sell.description': {
    id: 'comms.templates.starter.smsCrossSell.description',
    defaultMessage: 'Offer an additional line of coverage via text',
  },
  'sms-cancellation.name': {
    id: 'comms.templates.starter.smsCancellation.name',
    defaultMessage: 'Cancellation Alert',
  },
  'sms-cancellation.description': {
    id: 'comms.templates.starter.smsCancellation.description',
    defaultMessage: 'Urgent outreach when a policy status changes',
  },
  'sms-welcome.name': {
    id: 'comms.templates.starter.smsWelcome.name',
    defaultMessage: 'Welcome New Client',
  },
  'sms-welcome.description': {
    id: 'comms.templates.starter.smsWelcome.description',
    defaultMessage: 'Quick welcome text after a new policy is created',
  },
})

/** Iconos y símbolos: diseño, no texto (guía §17.3). */
export const ICON_ENVELOPE = '✉️'
export const ICON_CHAT = '💬'
export const CHECK_MARK = '✓'
export const PLUS_SIGN = '+'

/**
 * Token de personalización de ejemplo. **No se traduce**: lo interpreta el motor
 * de envío y traducirlo rompe el mensaje (guía, trampas del rule).
 */
export const VARIABLE_EXAMPLE = '{{first_name}}'
export const VARIABLE_NAME_TOKEN = '{{variable_name}}'
export const UNSUBSCRIBE_TOKEN = '{{unsubscribe_url}}'
export const CLIENT_NAME_TOKEN = '{{client_first_name}}'
export const TEST_EMAIL_PLACEHOLDER = 'you@email.com'
export const FONT_FAMILY_PLACEHOLDER = 'Arial, sans-serif'

/** Iconos de los botones del editor: diseño, no texto. */
export const ICON_BULB = '💡'
export const ICON_SAVE = '💾'
export const ICON_PLUS = '➕'
export const ICON_TRASH = '🗑️'
export const ICON_GEAR = '⚙️'

/** `{{policy_effective_date}}` — un token cualquiera renderizado como codigo. */
export const asToken = (name: string) => `{{${name}}}`

/** Antepone un espacio a un texto ya traducido (el rule marca el template literal suelto). */
export const withSpace = (text: string) => ` ${text}`

/** Flecha del boton de volver y estrellas del blueprint: diseno, no texto. */
export const ARROW_LEFT = '←'
export const STAR_FILLED = '⭐'
export const STAR_EMPTY = '☆'
export const ICON_EYE = '👁️'
export const ICON_VARIANTS = '📝'
/** Etiquetas de los botones de encabezado del editor y emoji del selector. */
export const HEADING_1 = 'H1'
export const HEADING_2 = 'H2'
export const HEADING_3 = 'H3'
export const EMOJI_SMILEY = '😀'

/** Pega un icono a un texto ya traducido sin que el rule marque el template literal. */
export const withIcon = (icon: string, label: string) => `${icon} ${label}`
